import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Stack, Button, Table, TableHead, TableBody, TableRow, TableCell, Chip } from '@mui/material'

const TRACKED_EVENTS = ['lobby:sync-start', 'lobby:sync-complete', 'lobby:sync-error', 'online', 'offline']

const EVENT_COLOR = {
  'lobby:sync-start': 'info',
  'lobby:sync-complete': 'success',
  'lobby:sync-error': 'error',
  online: 'success',
  offline: 'error',
}

let logIdCounter = 0

function SyncEventLogPanel() {
  const { t } = useTranslation()
  const [log, setLog] = useState([])
  const logRef = useRef(log)
  logRef.current = log

  const appendEntry = useCallback((eventName, detail) => {
    logIdCounter += 1
    setLog(prev => [{ id: logIdCounter, eventName, detail, timestamp: new Date() }, ...prev])
  }, [])

  useEffect(() => {
    const handlers = TRACKED_EVENTS.map(eventName => {
      const handler = (event) => {
        const detail = event?.detail?.message || event?.detail || null
        appendEntry(eventName, detail)
      }
      window.addEventListener(eventName, handler)
      return { eventName, handler }
    })
    return () => {
      handlers.forEach(({ eventName, handler }) => window.removeEventListener(eventName, handler))
    }
  }, [appendEntry])

  const clearLog = useCallback(() => setLog([]), [])

  return (
    <Stack spacing={3} className="audit-section">
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <h3>{t('audit_event_log_title')}</h3>
        <Button variant="outlined" onClick={clearLog} disabled={log.length === 0}>
          {t('audit_clear_log')}
        </Button>
      </Stack>

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>{t('audit_event_log_time')}</TableCell>
            <TableCell>{t('audit_event_log_event')}</TableCell>
            <TableCell>{t('audit_event_log_detail')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {log.map(entry => (
            <TableRow key={entry.id}>
              <TableCell>{entry.timestamp.toLocaleTimeString()}</TableCell>
              <TableCell>
                <Chip size="small" label={entry.eventName} color={EVENT_COLOR[entry.eventName] || 'default'} />
              </TableCell>
              <TableCell>{entry.detail ? String(entry.detail) : '—'}</TableCell>
            </TableRow>
          ))}
          {log.length === 0 && (
            <TableRow>
              <TableCell colSpan={3}>{t('audit_event_log_empty')}</TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </Stack>
  )
}

export default SyncEventLogPanel
